import { useEffect, useState } from "react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import ReauthModal from "@/components/ReauthModal";
import {
  ShieldCheck, ShieldAlert, KeyRound, AlertTriangle, FileText, FileSignature,
  Loader2, Check, Lock, Users, Download, FileWarning, Ban, ShieldQuestion, Plus, Trash2,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "border-border",
    good: "border-emerald-200 bg-emerald-50/40",
    warn: "border-amber-200 bg-amber-50/40",
    bad: "border-rose-200 bg-rose-50/40",
  };
  return (
    <div className={`rounded-lg border p-5 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs uppercase tracking-[0.05em] font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold font-heading">{value}</p>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/* The tone carries the honest reading, and the distinction matters:
   - `enforcing` means something is actually being stopped right now -> red.
   - a signed BAA on a live gate -> green.
   - an override -> amber, because it is time-limited and somebody has to come back to it.
   - no channel configured -> MUTED, never red. An outstanding BAA for a feature that
     does not exist is not a breach and must not read as one; showing it red was the
     original problem, where the Compliance Center displayed two controls that nothing
     in the codebase consulted. */
function GateBadge({ gate }) {
  const base = "shrink-0 text-xs font-medium rounded-full px-2.5 py-1 border";
  if (!gate.enforced_by) {
    return (
      <span className={`${base} border-border bg-secondary text-muted-foreground`}
            data-testid={`gate-badge-${gate.feature}`}>
        {gate.status_label}
      </span>
    );
  }
  if (gate.enforcing) {
    return (
      <span className={`${base} border-rose-200 bg-rose-50 text-rose-700`}
            data-testid={`gate-badge-${gate.feature}`}>
        {gate.status_label}
      </span>
    );
  }
  const tone = gate.overridden
    ? "border-amber-200 bg-amber-50 text-amber-800"
    : "border-emerald-200 bg-emerald-50 text-emerald-700";
  return (
    <span className={`${base} ${tone}`} data-testid={`gate-badge-${gate.feature}`}>
      {gate.status_label}
    </span>
  );
}

export default function Compliance() {
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [gates, setGates] = useState([]);
  const [risk, setRisk] = useState({ risks: [], reviewer: "", last_reviewed: "" });
  const [npp, setNpp] = useState({ content: "", version: 0 });
  const [savingBaa, setSavingBaa] = useState(false);
  const [savingNpp, setSavingNpp] = useState(false);
  const [savingRisk, setSavingRisk] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [reauthFor, setReauthFor] = useState(null); // feature pending override

  const load = () => {
    api.get("/compliance/overview").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
    api.get("/clinic/baa").then((r) => { setVendors(r.data.vendors); setGates(r.data.gates || []); }).catch(() => {});
    api.get("/clinic/npp").then((r) => setNpp(r.data)).catch(() => {});
    api.get("/clinic/risk-analysis").then((r) => setRisk(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleVendor = (i) => {
    const next = vendors.map((v, idx) => (idx === i ? { ...v, signed: !v.signed } : v));
    setVendors(next);
  };
  const saveBaa = async () => {
    setSavingBaa(true); setMsg(""); setErr("");
    try { await api.put("/clinic/baa", { vendors }); setMsg("BAA checklist saved."); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setSavingBaa(false);
  };
  const saveNpp = async () => {
    setSavingNpp(true); setMsg(""); setErr("");
    try { await api.put("/clinic/npp", { content: npp.content }); setMsg("Notice of Privacy Practices published."); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setSavingNpp(false);
  };

  const [snapBusy, setSnapBusy] = useState(false);
  const downloadSnapshot = async () => {
    setSnapBusy(true); setErr("");
    try {
      const r = await api.get("/compliance/snapshot.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `compliance-snapshot-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr("Could not generate snapshot."); }
    setSnapBusy(false);
  };

  if (!data) {
    return <div className="space-y-6" data-testid="compliance-loading"><div className="h-8 w-64 bg-secondary rounded animate-pulse" /><div className="grid sm:grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-28 bg-secondary rounded-lg animate-pulse" />)}</div></div>;
  }

  const auditTone = data.audit.intact ? "good" : "bad";
  const mfaTone = data.mfa.enabled === data.mfa.total_staff ? "good" : "warn";
  const bgTone = data.break_glass.active > 0 ? "warn" : "default";
  const baaTone = data.baa.signed === data.baa.total ? "good" : "warn";

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Compliance Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Live HIPAA posture for your clinic — audit integrity, access controls, disclosures and vendor agreements.</p>
        </div>
        <button onClick={downloadSnapshot} disabled={snapBusy} data-testid="snapshot-download-button" className="shrink-0 inline-flex items-center gap-2 border border-border rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
          {snapBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Compliance Snapshot (PDF)
        </button>
      </div>

      {msg && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{msg}</p>}
      {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="compliance-error">{err}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="card-audit" icon={data.audit.intact ? ShieldCheck : ShieldAlert} label="Audit Integrity" tone={auditTone} value={data.audit.intact ? "Verified" : `Broken #${data.audit.broken_at_seq}`} />
        <StatCard testid="card-mfa" icon={KeyRound} label="Staff MFA" tone={mfaTone} value={`${data.mfa.enabled}/${data.mfa.total_staff}`} />
        <StatCard testid="card-breakglass" icon={AlertTriangle} label="Active Break-glass" tone={bgTone} value={data.break_glass.active} />
        <StatCard testid="card-baa" icon={FileSignature} label="BAAs Signed" tone={baaTone} value={`${data.baa.signed}/${data.baa.total}`} />
        <StatCard testid="card-incidents" icon={FileWarning} label="Open Incidents" tone={(data.incidents?.open || 0) > 0 ? "warn" : "default"} value={data.incidents?.open ?? 0} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Staff MFA detail */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Staff Two-Factor Enrollment</h3></div>
          <div className="divide-y divide-border" data-testid="mfa-staff-list">
            {data.mfa.staff.map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{ROLE_LABELS[s.role] || s.role}</p></div>
                {s.mfa_enabled
                  ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5"><Check className="w-3 h-3" /> Enabled</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5"><Lock className="w-3 h-3" /> Not set up</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Privacy activity */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-emerald-600" /><h3 className="font-heading text-lg font-semibold">Privacy Activity</h3></div>
          <dl className="grid grid-cols-2 gap-4 text-sm" data-testid="privacy-activity">
            <div><dt className="text-muted-foreground">Disclosures logged</dt><dd className="text-xl font-bold font-heading">{data.privacy.disclosures}</dd></div>
            <div><dt className="text-muted-foreground">Access requests</dt><dd className="text-xl font-bold font-heading">{data.privacy.access_requests}</dd></div>
            <div><dt className="text-muted-foreground">Active authorizations</dt><dd className="text-xl font-bold font-heading">{data.privacy.active_authorizations}</dd></div>
            <div><dt className="text-muted-foreground">Patients w/ restrictions</dt><dd className="text-xl font-bold font-heading">{data.privacy.patients_with_restrictions}</dd></div>
          </dl>
        </div>
      </div>

      {/* BAA checklist */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1"><FileSignature className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Business Associate Agreements</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Track signed BAAs with every vendor that touches ePHI (45 CFR 164.308(b)).</p>
        <div className="divide-y divide-border" data-testid="baa-list">
          {vendors.map((v, i) => (
            <label key={i} className="flex items-center gap-3 py-2.5 cursor-pointer">
              <input type="checkbox" data-testid={`baa-toggle-${i}`} checked={v.signed} onChange={() => toggleVendor(i)} className="w-4 h-4 rounded border-input" />
              <div className="min-w-0"><p className="text-sm font-medium">{v.name}</p>{v.note && <p className="text-xs text-muted-foreground">{v.note}</p>}</div>
            </label>
          ))}
        </div>
        <button onClick={saveBaa} disabled={savingBaa} data-testid="baa-save-button" className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {savingBaa && <Loader2 className="w-4 h-4 animate-spin" />} Save checklist
        </button>
      </div>

      {/* Feature gates — what the BAA state is actually stopping, and what it is not */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Ban className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Feature gates</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          A gate blocks a feature until the vendor's BAA is signed. A gate with no
          channel behind it is listed for completeness and is not enforcing anything —
          it will begin to the moment that feature ships.
        </p>
        <div className="divide-y divide-border" data-testid="gate-list">
          {gates.map((g) => (
            <div key={g.feature} className="py-3" data-testid={`gate-${g.feature}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{g.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{g.vendor}</p>
                  {g.enforced_by && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Enforced at: {g.enforced_by}
                    </p>
                  )}
                </div>
                <GateBadge gate={g} />
              </div>

              {g.overridden && g.override && (
                <div
                  className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2"
                  data-testid={`gate-override-${g.feature}`}
                >
                  <p className="text-xs font-medium text-amber-900">
                    Temporary override — expires {fmtDate(g.override_expires_at)}
                    {typeof g.override_days_left === "number" && (
                      <> ({g.override_days_left} day{g.override_days_left === 1 ? "" : "s"} left)</>
                    )}
                  </p>
                  <p className="text-xs text-amber-900/80 mt-0.5">
                    Authorized by {g.override.authorized_by || "—"}
                    {g.override.role ? ` (${ROLE_LABELS[g.override.role] || g.override.role})` : ""}
                    {" — "}{g.override.reason}
                  </p>
                </div>
              )}

              {g.override_expired && (
                <div
                  className="mt-2 rounded-md border border-border bg-secondary px-3 py-2"
                  data-testid={`gate-override-expired-${g.feature}`}
                >
                  <p className="text-xs font-medium">
                    A previous override has expired and is no longer honoured.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* NPP editor */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1"><FileText className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Notice of Privacy Practices</h3>
          {npp.version > 0 && <span className="ml-1 text-xs font-medium text-muted-foreground bg-secondary rounded-full px-2.5 py-0.5">v{npp.version}</span>}</div>
        <p className="text-sm text-muted-foreground mb-4">Published to your patient portal (45 CFR 164.520). Markdown supported.</p>
        <textarea
          data-testid="npp-editor"
          value={npp.content}
          onChange={(e) => setNpp({ ...npp, content: e.target.value })}
          rows={8}
          placeholder="# Notice of Privacy Practices&#10;Describe how your clinic uses and protects patient health information…"
          className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-ring"
        />
        <button onClick={saveNpp} disabled={savingNpp || !npp.content.trim()} data-testid="npp-save-button" className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {savingNpp && <Loader2 className="w-4 h-4 animate-spin" />} Publish
        </button>
      </div>
    </div>
  );
}
